import React, { useState } from "react";
import axios from "axios";
import Swal from "sweetalert2";
import { Button, Spinner } from "reactstrap";
import { DOCUMENTOS, headersAuth } from "./revalidacionUtils";

const URL_UP = process.env.REACT_APP_URL_UPMEDIA_DOCUMENTO;
const URL_FIND = process.env.REACT_APP_URL_FINDMEDIA_DOCUMENTO;
const URL_DEL = process.env.REACT_APP_URL_DELMEDIA_DOCUMENTO;

const esOffice = (f) => ["xls", "xlsx", "xlsm", "doc", "docx", "csv"].includes(String(f || "").toLowerCase());

/**
 * Archivos del contenedor por tipo (como el modal "Archivos" de ShiffCargo): una fila por documento
 * con ver / subir / borrar. Usa las mismas carpetas y banderas que ListadoContenedores.
 *  props: contenedorId, docs ({tipo: "Si"|"No"}), puedeSubir, onChange(docs), tipos (opcional: solo esos documentos)
 */
export default function DocumentosContenedor({ contenedorId, docs = {}, puedeSubir, onChange, tipos }) {
  const DOCS = tipos ? DOCUMENTOS.filter((d) => tipos.includes(d.tipo)) : DOCUMENTOS;
  const [abierto, setAbierto] = useState(null); // tipo cuyo visor está abierto
  const [archivos, setArchivos] = useState({}); // tipo -> recursos de Cloudinary
  const [cargando, setCargando] = useState(null);
  const [subiendo, setSubiendo] = useState(null);

  const flags = { ...docs };
  const tiene = (tipo) => flags[tipo] === "Si";
  const grupos = [...new Set(DOCS.map((d) => d.grupo))];
  const completos = DOCS.filter((d) => tiene(d.tipo)).length;

  const ver = async (tipo) => {
    if (abierto === tipo) return setAbierto(null);
    setAbierto(tipo);
    setCargando(tipo);
    try {
      const { data } = await axios.get(`${URL_FIND}/${tipo}/${contenedorId}`, { headers: headersAuth() });
      setArchivos((a) => ({ ...a, [tipo]: data.resources || [] }));
    } catch (err) {
      setArchivos((a) => ({ ...a, [tipo]: [] }));
    }
    setCargando(null);
  };

  const subir = async (tipo, files) => {
    if (!files || !files.length) return;
    setSubiendo(tipo);
    try {
      for (const f of files) {
        const fd = new FormData();
        fd.append("file", f);
        fd.append("contenedor", contenedorId);
        fd.append("tipo", tipo);
        await axios.post(URL_UP, fd, { headers: { ...headersAuth(), "Content-Type": "multipart/form-data" } });
      }
      onChange && onChange({ ...flags, [tipo]: "Si" });
      Swal.fire({ icon: "success", title: "Archivo cargado", timer: 1200, showConfirmButton: false });
      if (abierto === tipo) {
        const { data } = await axios.get(`${URL_FIND}/${tipo}/${contenedorId}`, { headers: headersAuth() });
        setArchivos((a) => ({ ...a, [tipo]: data.resources || [] }));
      }
    } catch (err) {
      Swal.fire({ icon: "error", title: "No se pudo subir", text: err?.response?.data?.message || err.message });
    }
    setSubiendo(null);
  };

  const borrar = async (tipo, item) => {
    const ok = await Swal.fire({
      title: "¿Borrar archivo?",
      text: item.original_filename || item.public_id,
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Sí, borrar",
      cancelButtonText: "Cancelar",
    });
    if (!ok.value) return;
    try {
      const { data } = await axios.post(`${URL_DEL}/${tipo}/${contenedorId}/${item.asset_id}`, {}, { headers: headersAuth() });
      const restantes = (archivos[tipo] || []).filter((x) => x.asset_id !== item.asset_id);
      setArchivos((a) => ({ ...a, [tipo]: restantes }));
      if (!data.quedan) onChange && onChange({ ...flags, [tipo]: "No" });
    } catch (err) {
      Swal.fire({ icon: "error", title: "No se pudo borrar", text: err?.response?.data?.message || err.message });
    }
  };

  return (
    <div className="rv-docs">
      <div className="rv-docs-head">
        <span><i className="fas fa-folder-open" /> {completos} / {DOCS.length} documentos cargados</span>
        <div className="rc-progress" style={{ width: 160, margin: 0 }}>
          <span style={{ width: `${(completos / DOCS.length) * 100}%` }} />
        </div>
      </div>
      {grupos.map((g) => (
        <div key={g} className="rv-docs-group">
          <div className="rv-docs-group-title">{g}</div>
          {DOCS.filter((d) => d.grupo === g).map((d) => (
            <div key={d.tipo} className={`rv-doc${tiene(d.tipo) ? " ok" : ""}${d.destacado ? " destacado" : ""}`}>
              <div className="rv-doc-row">
                <span className="rv-doc-icon"><i className={d.icon} /></span>
                <span className="rv-doc-label">
                  {d.label}
                  {d.destacado ? <small> · requerido</small> : null}
                </span>
                <span className={`badge-soft badge-soft-${tiene(d.tipo) ? "success" : "secondary"}`}>
                  {tiene(d.tipo) ? "Cargado" : "Pendiente"}
                </span>
                <div className="rv-doc-actions">
                  <Button size="sm" color="primary" disabled={!tiene(d.tipo)} onClick={() => ver(d.tipo)} title="Ver archivos">
                    <i className={abierto === d.tipo ? "fas fa-eye-slash" : "fas fa-eye"} />
                  </Button>
                  <label className={`btn btn-success btn-sm rv-upload${!puedeSubir || subiendo ? " disabled" : ""}`} title="Subir archivo">
                    {subiendo === d.tipo ? <Spinner size="sm" /> : <i className="fas fa-upload" />}
                    <input
                      type="file"
                      multiple
                      hidden
                      disabled={!puedeSubir || !!subiendo}
                      onChange={(e) => { subir(d.tipo, Array.from(e.target.files || [])); e.target.value = ""; }}
                    />
                  </label>
                </div>
              </div>
              {abierto === d.tipo ? (
                <div className="rv-doc-files">
                  {cargando === d.tipo ? (
                    <div className="td-muted"><Spinner size="sm" /> Cargando…</div>
                  ) : (archivos[d.tipo] || []).length === 0 ? (
                    <div className="td-muted">Sin archivos</div>
                  ) : (
                    (archivos[d.tipo] || []).map((item) => (
                      <div key={item.asset_id} className="rv-file">
                        <a href={item.secure_url} target="_blank" rel="noopener noreferrer" className="rv-file-thumb">
                          {item.format === "pdf" ? (
                            <i className="far fa-file-pdf" style={{ color: "#dc2626" }} />
                          ) : esOffice(item.format) || item.resource_type === "raw" ? (
                            <i className="far fa-file-excel" style={{ color: "#16a34a" }} />
                          ) : (
                            <img src={item.secure_url} alt="" />
                          )}
                        </a>
                        <div className="rv-file-name" title={item.original_filename}>
                          {item.original_filename || item.public_id}.{item.format}
                          <small>{(item.created_at || "").slice(0, 10)}</small>
                        </div>
                        {puedeSubir ? (
                          <Button size="sm" color="danger" title="Borrar" onClick={() => borrar(d.tipo, item)}>
                            <i className="fas fa-trash-alt" />
                          </Button>
                        ) : null}
                      </div>
                    ))
                  )}
                </div>
              ) : null}
            </div>
          ))}
        </div>
      ))}
    </div>
  );
}
